import { useEffect, useMemo, useState } from "react";
import { X, Search, Plus, Trash2, Package, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const METHODS = [["cash", "Cash"], ["card", "Credit Card"], ["cheque", "Cheque"], ["e_transfer", "e-Transfer"], ["bank_transfer", "Bank Transfer"], ["wallet", "Wallet"]];
const HST = 0.13; // Ontario HST estimate for preview; server recomputes authoritative total on save.
const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OrderCreateModal({ customers, onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [pq, setPq] = useState("");
  const [lines, setLines] = useState([]); // {product_id, name, uom, price, cost, stock, qty}
  const [customerId, setCustomerId] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [method, setMethod] = useState("cash");
  const [cardNumber, setCardNumber] = useState("");
  const [payStatus, setPayStatus] = useState("paid");
  const [partialPaid, setPartialPaid] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products?limit=10000").then(({ data }) => setProducts(data)).catch(() => {}); }, []);

  const results = useMemo(() => {
    const s = pq.trim().toLowerCase();
    if (!s) return [];
    return products.filter((p) => p.name.toLowerCase().includes(s)
      || (p.part_number || "").toLowerCase().includes(s)
      || (p.uoms || []).some((u) => (u.part_number || "").toLowerCase().includes(s))).slice(0, 8);
  }, [pq, products]);

  const uomOptions = (p) => (p.uoms && p.uoms.length ? p.uoms.map((u) => ({ unit: u.unit, price: Number(u.price) || 0, cost: Number(u.cost) || 0, stock: Number(u.in_stock) || 0, part: u.part_number || "" }))
    : [{ unit: "", price: Number(p.price) || 0, cost: Number(p.cost) || 0, stock: Number(p.stock) || 0, part: p.part_number || "" }]);

  const addLine = (p) => {
    const u = uomOptions(p)[0];
    setLines((l) => [...l, { product_id: p.id, name: p.name, uoms: uomOptions(p), uom: u.unit, price: u.price, cost: u.cost, stock: u.stock, qty: 1 }]);
    setPq("");
  };
  const setUom = (i, unit) => setLines((l) => l.map((x, k) => { if (k !== i) return x; const u = x.uoms.find((o) => o.unit === unit) || x.uoms[0]; return { ...x, uom: unit, price: u.price, cost: u.cost, stock: u.stock }; }));
  const setQty = (i, q) => setLines((l) => l.map((x, k) => k === i ? { ...x, qty: Math.max(1, parseInt(q || 1, 10)) } : x));
  const removeLine = (i) => setLines((l) => l.filter((_, k) => k !== i));

  const subtotal = lines.reduce((s, x) => s + x.price * x.qty, 0);
  const discount = subtotal * (Number(discountPct) || 0) / 100;
  const taxable = Math.max(subtotal - discount, 0);
  const tax = taxable * HST;
  const total = round2(taxable + tax);
  const amountPaid = payStatus === "paid" ? total : payStatus === "unpaid" ? 0 : Math.min(Number(partialPaid) || 0, total);
  const balance = round2(total - amountPaid);

  const save = async () => {
    if (lines.length === 0) return toast.error("Add at least one product");
    for (const x of lines) if (x.qty > x.stock) return toast.error(`Only ${x.stock} in stock for ${x.name} (${x.uom || "base"})`);
    if (method === "card" && !cardNumber.trim()) return toast.error("Enter a card number for card payments");
    setSaving(true);
    try {
      const body = {
        items: lines.map((x) => ({ product_id: x.product_id, quantity: x.qty, uom: x.uom })),
        discount_pct: Number(discountPct) || 0, province: "", source: "manual",
        payment_method: method, customer_id: customerId || null,
        shipping: { method: "pickup", cost: 0 },
      };
      if (method === "card") body.card_number = cardNumber.trim();
      const { data: order } = await api.post("/orders", body);
      // Adjust the linked invoice's payment to reflect the chosen status (uses the existing invoice endpoint).
      if (order.invoice_id && payStatus !== "paid") {
        await api.put(`/invoices/${order.invoice_id}`, { amount_paid: amountPaid, ...(notes ? { notes } : {}) });
      } else if (order.invoice_id && notes) {
        await api.put(`/invoices/${order.invoice_id}`, { notes });
      }
      toast.success(`Order ${order.order_number} created (${money(order.total)})`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-50 rounded-2xl w-full max-w-4xl my-6 shadow-2xl" data-testid="order-create-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white rounded-t-2xl sticky top-0 z-10">
          <div><h2 className="font-heading text-lg font-bold text-zinc-900">Create Order</h2><p className="text-xs text-zinc-400">Demodog World Inc. · ERP · Ontario</p></div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="p-6 grid lg:grid-cols-3 gap-5">
          {/* Left: order info + items */}
          <div className="lg:col-span-2 space-y-5">
            <Card title="Order Information" icon={<User size={15} className="text-zinc-400" />}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Customer">
                  <select data-testid="oc-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="oc-inp">
                    <option value="">Walk-in customer</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.name}</option>)}
                  </select>
                </Field>
                <Field label="Order Date"><input disabled value={new Date().toLocaleDateString("en-CA")} className="oc-inp bg-zinc-100 text-zinc-500" /></Field>
                <Field label="Notes" full><input data-testid="oc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional order notes" className="oc-inp" /></Field>
              </div>
            </Card>

            <Card title="Products / Items" icon={<Package size={15} className="text-zinc-400" />}>
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input data-testid="oc-product-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search by product name or part # / SKU…" className="oc-inp pl-9" />
                {results.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-zinc-200 rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="oc-product-results">
                    {results.map((p) => (
                      <button key={p.id} onClick={() => addLine(p)} data-testid={`oc-add-${p.id}`} className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center justify-between gap-2 border-b border-zinc-50 last:border-0">
                        <span className="text-sm font-medium text-zinc-800 truncate">{p.name}</span>
                        <span className="text-xs text-zinc-400 whitespace-nowrap">{p.part_number || (p.uoms || [])[0]?.part_number || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {lines.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center">No items added yet. Search above to add products.</p> : (
                <div className="space-y-2" data-testid="oc-lines">
                  {lines.map((x, i) => (
                    <div key={i} className="bg-white border border-zinc-200 rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`oc-line-${i}`}>
                      <div className="flex-1 min-w-[140px]"><p className="text-sm font-semibold text-zinc-800 truncate">{x.name}</p><p className="text-[11px] text-zinc-400">Cost {money(x.cost)} · {x.stock} in stock</p></div>
                      {x.uoms.length > 1 && <select value={x.uom} onChange={(e) => setUom(i, e.target.value)} className="oc-inp !h-9 !w-32 text-sm">{x.uoms.map((u) => <option key={u.unit} value={u.unit}>{u.unit || "Base"}</option>)}</select>}
                      <input type="number" min="1" value={x.qty} onChange={(e) => setQty(i, e.target.value)} data-testid={`oc-qty-${i}`} className="oc-inp !h-9 !w-20 text-sm" />
                      <span className="text-sm text-zinc-500 w-20 text-right">{money(x.price)}</span>
                      <span className="text-sm font-bold text-zinc-900 w-24 text-right">{money(x.price * x.qty)}</span>
                      <button onClick={() => removeLine(i)} data-testid={`oc-remove-${i}`} className="h-8 w-8 rounded-lg border border-red-200 text-red-600 flex items-center justify-center active:scale-90"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right: totals + payment */}
          <div className="space-y-5">
            <Card title="Order Totals">
              <div className="space-y-2 text-sm">
                <Row label="Subtotal" value={money(subtotal)} />
                <div className="flex items-center justify-between"><span className="text-zinc-500">Discount %</span><input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} data-testid="oc-discount" className="oc-inp !h-9 !w-24 text-sm text-right" /></div>
                <Row label="HST (13%)" value={money(tax)} />
                <div className="border-t border-zinc-200 pt-2 flex items-center justify-between"><span className="font-bold text-zinc-900">Grand Total</span><span data-testid="oc-total" className="font-heading font-extrabold text-lg">{money(total)}</span></div>
              </div>
            </Card>

            <Card title="Payment">
              <div className="space-y-3">
                <Field label="Payment method">
                  <select data-testid="oc-method" value={method} onChange={(e) => setMethod(e.target.value)} className="oc-inp">{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </Field>
                {method === "card" && <Field label="Card number"><input data-testid="oc-card" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="4242 4242 4242 4242" className="oc-inp" /></Field>}
                <Field label="Payment status">
                  <select data-testid="oc-pay-status" value={payStatus} onChange={(e) => setPayStatus(e.target.value)} className="oc-inp"><option value="paid">Paid in full</option><option value="partially_paid">Partially paid</option><option value="unpaid">Unpaid</option></select>
                </Field>
                {payStatus === "partially_paid" && <Field label="Amount paid"><input type="number" min="0" value={partialPaid} onChange={(e) => setPartialPaid(e.target.value)} data-testid="oc-amount-paid" className="oc-inp" /></Field>}
                <div className="flex items-center justify-between text-sm pt-1"><span className="text-zinc-500">Balance due</span><span data-testid="oc-balance" className={`font-bold ${balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{money(balance)}</span></div>
              </div>
            </Card>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 bg-white rounded-b-2xl flex gap-3 sticky bottom-0">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl border border-zinc-200 font-semibold">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="oc-save" className="flex-1 h-12 rounded-xl bg-brand text-white font-semibold disabled:opacity-60 flex items-center justify-center gap-2">{saving ? "Saving…" : <><Plus size={17} />Create Order</>}</button>
        </div>
      </div>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
function Card({ title, icon, children }) { return <div className="bg-white border border-zinc-200 rounded-2xl p-4"><div className="flex items-center gap-2 mb-3">{icon}<h3 className="font-heading text-sm font-bold text-zinc-900">{title}</h3></div>{children}</div>; }
function Field({ label, children, full }) { return <div className={full ? "sm:col-span-2" : ""}><label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{label}</label><div className="mt-1.5">{children}</div></div>; }
function Row({ label, value }) { return <div className="flex items-center justify-between"><span className="text-zinc-500">{label}</span><span className="text-zinc-800 font-medium">{value}</span></div>; }
